'use client'

import { useEffect, useId } from 'react'

/**
 * Runs a reference page's own script (public/pixelmojo-rebuild) after the
 * page mounts, exactly as the reference does: the markup is the reference's,
 * so the script finds the same ids and classes. Injected as an inline script
 * element (the marketing CSP allows inline scripts; eval is not used), and
 * re-run on every mount so client-side navigation re-arms it.
 *
 * Everything the script registers on the page-wide scope (window listeners,
 * timers, animation frames and observers) goes through a per-mount registry,
 * so leaving the page tears it down instead of leaking into the next one.
 * Listeners on the page's own elements go with the elements.
 *
 * The script runs once the mount has settled (a zero-delay timer the cleanup
 * cancels). React's development Strict Mode mounts, cleans up and mounts
 * again on the same DOM; a synchronous run bound every element listener
 * twice there, so toggles cancelled themselves and funnel events fired twice.
 */
const PRELUDE = `
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches || document.body.classList.contains('reduce-motion');
const addEventListener = (...a) => { window.addEventListener(...a); __reg.push(() => window.removeEventListener(...a)); };
const removeEventListener = (...a) => window.removeEventListener(...a);
const setInterval = (...a) => { const id = window.setInterval(...a); __reg.push(() => window.clearInterval(id)); return id; };
const setTimeout = (...a) => { const id = window.setTimeout(...a); __reg.push(() => window.clearTimeout(id)); return id; };
const requestAnimationFrame = f => __dead() ? 0 : window.requestAnimationFrame(t => { if (!__dead()) f(t); });
const __observe = Base => typeof Base === 'function' ? class extends Base { constructor(...a) { super(...a); __reg.push(() => this.disconnect()); } } : Base;
const IntersectionObserver = __observe(window.IntersectionObserver);
const ResizeObserver = __observe(window.ResizeObserver);
const MutationObserver = __observe(window.MutationObserver);
`

export default function RefScript({ code }: { code: string }) {
  const id = useId()
  useEffect(() => {
    type Registry = Record<string, { reg: Array<() => void>; dead: boolean }>
    const w = window as unknown as { __pmRef?: Registry }
    const registry = (w.__pmRef ??= {})
    registry[id] = { reg: [], dead: false }
    const key = JSON.stringify(id)
    const el = document.createElement('script')
    el.textContent = `(() => {
const __slot = window.__pmRef[${key}];
const __reg = __slot.reg;
const __dead = () => __slot.dead;
${PRELUDE}
try {
(() => {
${code}
})();
} catch (e) { console.error('[reference script]', e) }
})();`
    const run = window.setTimeout(() => document.body.appendChild(el), 0)
    return () => {
      window.clearTimeout(run)
      const slot = registry[id]
      if (slot) {
        slot.dead = true
        slot.reg.forEach(fn => {
          try {
            fn()
          } catch {
            /* already gone */
          }
        })
        delete registry[id]
      }
      el.remove()
    }
  }, [code, id])
  return null
}
